import React, { useState } from "react";
import { Check, ChevronRight, Mail, PenLine } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { isSameEmail, isValidEmailOtp, syncPublicUserProfile } from "../../lib/userProfile";
import ProfileAvatar from "./ProfileAvatar";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CurrentProfilePanel = ({ userId, userName, userEmail, onNameClick, onProfileUpdated }) => {
  const [isEmailEditorOpen, setIsEmailEditorOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [confirmingCode, setConfirmingCode] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const resetEmailEditor = () => {
    setCodeSent(false);
    setVerificationCode("");
    setMessage("");
    setError("");
  };

  const handleEmailRowClick = () => {
    setIsEmailEditorOpen((prev) => {
      const next = !prev;
      if (!next) {
        setNewEmail("");
        resetEmailEditor();
      } else {
        resetEmailEditor();
      }
      return next;
    });
  };

  const sendEmailChangeCode = async () => {
    const targetEmail = newEmail.trim();
    if (!EMAIL_PATTERN.test(targetEmail)) {
      setError("Please enter a valid email address.");
      setMessage("");
      return;
    }

    if (isSameEmail(targetEmail, userEmail)) {
      setError("Enter a different email address than the one already on this account.");
      setMessage("");
      return;
    }

    setSendingCode(true);
    setError("");
    setMessage("");

    try {
      const { error: updateError } = await supabase.auth.updateUser({ email: targetEmail });
      if (updateError) throw updateError;

      setCodeSent(true);
      setMessage(`A 6-digit verification code was sent to ${targetEmail}.`);
    } catch (updateErr) {
      setError(updateErr.message || "Unable to send verification code.");
    } finally {
      setSendingCode(false);
    }
  };

  const confirmEmailChange = async () => {
    const targetEmail = newEmail.trim();
    const token = verificationCode.trim();

    if (!isValidEmailOtp(token)) {
      setError("Enter the 6-digit code sent to your new email.");
      setMessage("");
      return;
    }

    setConfirmingCode(true);
    setError("");
    setMessage("");

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: targetEmail,
        token,
        type: "email_change",
      });
      if (verifyError) throw verifyError;

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const confirmedEmail = user?.email || targetEmail;
      const confirmedName =
        user?.user_metadata?.full_name ||
        user?.user_metadata?.name ||
        userName;

      await syncPublicUserProfile(supabase, {
        id: user?.id || userId,
        email: confirmedEmail,
        full_name: confirmedName,
      });

      onProfileUpdated?.({
        full_name: confirmedName,
        email: confirmedEmail,
      });
      setNewEmail("");
      setVerificationCode("");
      setCodeSent(false);
      setIsEmailEditorOpen(false);
      setMessage("Email updated successfully.");
    } catch (verifyErr) {
      setError(verifyErr.message || "Unable to verify the email code.");
    } finally {
      setConfirmingCode(false);
    }
  };

  return (
    <div className="w-full min-w-0 text-left">
      <div className="flex items-center gap-4 sm:gap-5">
        <ProfileAvatar />
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Current profile</p>
          <h2 className="mt-1 text-2xl font-semibold leading-tight tracking-tight text-white break-words">
            {userName || "Your Profile"}
          </h2>
          <p className="mt-1 text-sm text-white/55 break-words">{userEmail || "No email available"}</p>
        </div>
      </div>

      <div className="mt-6 w-full space-y-3">
        <button
          type="button"
          onClick={onNameClick}
          className="flex w-full items-center justify-between gap-4 rounded-xl bg-[#1a1a1a] border border-white/10 px-4 py-4 text-left text-white/90 transition hover:bg-white/5"
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10 text-white/90">
              <PenLine size={16} />
            </span>
            <span className="text-sm font-medium">Name</span>
          </span>
          <span className="flex min-w-0 items-center gap-2 text-sm text-white/70">
            <span className="truncate">{userName || "Add name"}</span>
            <ChevronRight size={16} className="shrink-0" />
          </span>
        </button>

        <button
          type="button"
          onClick={handleEmailRowClick}
          className="flex w-full items-center justify-between gap-4 rounded-xl bg-[#1a1a1a] border border-white/10 px-4 py-4 text-left text-white/90 transition hover:bg-white/5"
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10 text-white/90">
              <Mail size={16} />
            </span>
            <span className="text-sm font-medium">Email</span>
          </span>
          <span className="flex min-w-0 items-center gap-2 text-sm text-white/70">
            <span className="truncate">{userEmail || "Add email"}</span>
            <ChevronRight size={16} className="shrink-0" />
          </span>
        </button>

        {isEmailEditorOpen && (
          <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-4 sm:p-5">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-gray-400">
              New email address
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              placeholder={userEmail || "Enter new email"}
              disabled={codeSent}
              className="w-full rounded-md border border-white/10 bg-[#121212] px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-maroon disabled:opacity-60"
            />

            {!codeSent ? (
              <button
                type="button"
                onClick={sendEmailChangeCode}
                disabled={sendingCode}
                className="mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded px-6 py-2.5 text-sm font-semibold text-white bg-brand-maroon hover:bg-red-700 disabled:opacity-60"
              >
                <Check size={16} />
                {sendingCode ? "Sending..." : "Send verification code"}
              </button>
            ) : (
              <>
                <label className="mb-2 mt-4 block text-xs font-semibold uppercase tracking-widest text-gray-400">
                  6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="w-full max-w-md rounded-md border border-white/10 bg-[#121212] px-3 py-2.5 text-center text-lg tracking-[0.4em] text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-maroon"
                />

                <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={sendEmailChangeCode}
                    disabled={sendingCode}
                    className="inline-flex min-h-10 items-center justify-center rounded border border-white/15 px-6 py-2.5 text-sm font-semibold text-white/80 transition hover:bg-white/5 disabled:opacity-60"
                  >
                    {sendingCode ? "Sending..." : "Resend code"}
                  </button>
                  <button
                    type="button"
                    onClick={confirmEmailChange}
                    disabled={confirmingCode}
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded px-6 py-2.5 text-sm font-semibold text-white bg-brand-maroon hover:bg-red-700 disabled:opacity-60"
                  >
                    <Check size={16} />
                    {confirmingCode ? "Confirming..." : "Confirm email"}
                  </button>
                </div>
              </>
            )}

            <p className="mt-3 text-xs leading-relaxed text-gray-400">
              A 6-digit code will be sent to the new email. The current email stays active until that code is confirmed.
            </p>
          </div>
        )}

        {message ? <p className="text-sm text-emerald-400">{message}</p> : null}
        {error ? <p className="text-sm text-red-400">{error}</p> : null}
      </div>
    </div>
  );
};

export default CurrentProfilePanel;
